import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Plus, Storefront } from "@/components/icons";
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Skeleton,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { MarketEventStatus } from "@/server/platform/db/schema/event-sales";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useEventSalesQuery } from "../api/queries";
import { EVENT_STATUS_LABELS } from "../lib/labels";
import { CreateEventDialog } from "../ui/CreateEventDialog";
import { touchControlClass } from "../ui/touch-fields";

const FILTERS: Array<{ value: MarketEventStatus | "all"; label: string }> = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "open", label: "ກຳລັງຂາຍ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "closed", label: "ປິດງານ" },
];

function eventWhen(startsOn: string, endsOn: string | null) {
  if (!endsOn || endsOn === startsOn) return formatDateLocal(startsOn);
  return `${formatDateLocal(startsOn)} – ${formatDateLocal(endsOn)}`;
}

export function EventSalesPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission("event_sales:write");
  const [status, setStatus] = useState<MarketEventStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const list = useEventSalesQuery({
    status: status === "all" ? undefined : status,
    search: search.trim() || undefined,
    limit: 50,
  });
  const rows = list.data?.data ?? [];

  return (
    <>
      <Header>
        <h1 className="truncate font-display text-lg">ອອກງານ</h1>
      </Header>
      <Main>
        <div
          className={
            canWrite
              ? "mx-auto flex w-full max-w-3xl flex-col gap-4 pb-24 md:pb-0"
              : "mx-auto flex w-full max-w-3xl flex-col gap-4"
          }
        >
          <Input
            className={touchControlClass}
            value={search}
            placeholder="ຄົ້ນຊື່ງານ"
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {FILTERS.map((filter) => (
              <Button
                key={filter.value}
                type="button"
                variant={status === filter.value ? "default" : "outline"}
                className="h-11 shrink-0"
                onClick={() => setStatus(filter.value)}
              >
                {filter.label}
              </Button>
            ))}
            {canWrite ? (
              <Button
                type="button"
                className="ml-auto hidden h-11 md:inline-flex"
                onClick={() => setCreating(true)}
              >
                <Plus />
                ສ້າງງານ
              </Button>
            ) : null}
          </div>
          {list.isLoading ? (
            <Skeleton className="h-28 w-full" />
          ) : rows.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Storefront />
                </EmptyMedia>
                <EmptyTitle>ຍັງບໍ່ມີງານ</EmptyTitle>
                <EmptyDescription>ສ້າງງານເພື່ອບັນທຶກການຂາຍໜ້າງານ</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ul className="flex flex-col gap-3">
              {rows.map((event) => (
                <li key={event.id}>
                  <button
                    type="button"
                    className="brand-card flex w-full flex-col gap-3 p-4 text-left"
                    onClick={() =>
                      nav({
                        to: "/app/event-sales/$id",
                        params: { id: event.id },
                      })
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-medium text-base leading-snug">
                        {event.name}
                      </span>
                      <Badge variant="secondary" className="shrink-0">
                        {EVENT_STATUS_LABELS[event.status]}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground text-xs leading-relaxed">
                      {event.eventNumber}
                      {event.place ? ` · ${event.place}` : ""}
                      <br />
                      {eventWhen(event.startsOn, event.endsOn)}
                    </span>
                    <span className="flex items-baseline justify-between gap-3 border-t pt-3">
                      <span className="text-muted-foreground text-xs">
                        ກຳໄລ
                      </span>
                      <span className="font-display text-xl tabular-nums">
                        {formatLak(event.profit)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Main>
      {canWrite ? (
        <div className="fixed inset-x-0 bottom-(--mobile-bottom-nav,0px) z-30 border-t bg-background/95 p-3 backdrop-blur md:hidden">
          <Button
            type="button"
            className="h-12 w-full text-base"
            onClick={() => setCreating(true)}
          >
            <Plus />
            ສ້າງງານ
          </Button>
        </div>
      ) : null}
      <CreateEventDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}
